"use client";

import { motion } from "framer-motion";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { formatPlanPrice, formatYearlyHelper } from "./pricing-display";

export type BillingCycle = "monthly" | "yearly";

export type PlanConfig = {
  title: string;
  translationKey: string;
  pricing: {
    monthly: {
      usdAmount: number;
      priceDescription: string;
      buttonText: string;
      buttonLink: string;
      requiresBillingSelection: boolean;
      helperText: string;
    };
    yearly: {
      usdAmount: number;
      annualUsdAmount: number;
      priceDescription: string;
      buttonText: string;
      buttonLink: string;
      requiresBillingSelection: boolean;
      helperText: string;
    };
  };
  features: string[];
  planId: string;
  highlight?: boolean;
  recommended?: boolean;
};

export interface PricingSectionProps {
  billingCycle?: BillingCycle; // Optional - if not provided, component manages its own state
  onBillingCycleChange?: (cycle: BillingCycle) => void;
  onPlanSelect?: (planId: string, buttonLink: string, requiresBillingSelection: boolean) => void;
  selectedPlan?: string | null;
  compact?: boolean;
  className?: string;
}

export function PricingSection({
  billingCycle: externalBillingCycle,
  onBillingCycleChange,
  onPlanSelect: externalOnPlanSelect,
  selectedPlan: externalSelectedPlan,
  compact = false,
  className = "",
}: PricingSectionProps) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const [internalBillingCycle, setInternalBillingCycle] = useState<BillingCycle>("monthly");
  const [internalSelectedPlan, setInternalSelectedPlan] = useState<string | null>(null);

  // Use external billing cycle if provided, otherwise use internal state
  // This allows the component to manage its own state when used standalone
  const billingCycle = externalBillingCycle ?? internalBillingCycle;
  const selectedPlan = externalSelectedPlan ?? internalSelectedPlan;
  
  // Determine if billing cycle toggle should be shown
  // Show toggle if compact is false OR if no external billing cycle is provided (self-managed)
  const showBillingToggle = !compact || externalBillingCycle === undefined;

  const handleBillingCycleChange = (cycle: BillingCycle) => {
    if (onBillingCycleChange) {
      onBillingCycleChange(cycle);
    } else {
      setInternalBillingCycle(cycle);
    }
    setInternalSelectedPlan(null);
  };

  const handlePlanSelect = (
    planId: string,
    buttonLink: string,
    requiresBillingSelection: boolean
  ) => {
    if (externalOnPlanSelect) {
      externalOnPlanSelect(planId, buttonLink, requiresBillingSelection);
      return;
    }

    // Default implementation - route through unified checkout flow
    if (typeof window !== "undefined" && (planId === "pro" || planId === "pro-plus")) {
      setInternalSelectedPlan(planId);
      const plan = planId === "pro" ? "pro" : "pro_plus";
      router.push(`/checkout?plan=${plan}`);
      return;
    }

    const destination =
      requiresBillingSelection && planId !== "free"
        ? `${buttonLink}?billing=${billingCycle}`
        : buttonLink;

    setInternalSelectedPlan(planId);
    router.push(destination);
  };

  useEffect(() => {
    setInternalSelectedPlan(null);
  }, [billingCycle]);

  const DEFAULT_PLANS: PlanConfig[] = [
    {
      title: "Free",
      translationKey: "free",
      pricing: {
        monthly: { usdAmount: 0, priceDescription: "per month", buttonText: "Get Started", buttonLink: "/auth/sign-up", requiresBillingSelection: false, helperText: "" },
        yearly:  { usdAmount: 0, annualUsdAmount: 0, priceDescription: "per month", buttonText: "Get Started", buttonLink: "/auth/sign-up", requiresBillingSelection: false, helperText: "" },
      },
      features: ["30 credits you can spend for", "Brainstorm how to grow your profile", "Analyze why contents perform well / not", "Generate content ideas", "Ask for any advice you want!"],
      planId: "free",
    },
    {
      title: "Pro",
      translationKey: "pro",
      pricing: {
        monthly: { usdAmount: 20, priceDescription: "per month", buttonText: "Select Plan", buttonLink: "/payment/pro", requiresBillingSelection: true, helperText: "" },
        yearly:  { usdAmount: 16, annualUsdAmount: 192, priceDescription: "per month",    buttonText: "Select Plan", buttonLink: "/payment/pro", requiresBillingSelection: true, helperText: "" },
      },
      features: ["100 credits you can spend for", "Brainstorm how to grow your profile", "Analyze why contents perform well / not", "Generate content ideas", "Ask for any advice you want!"],
      highlight: true,
      recommended: true,
      planId: "pro",
    },
    {
      title: "Pro+",
      translationKey: "pro_plus",
      pricing: {
        monthly: { usdAmount: 60, priceDescription: "per month", buttonText: "Select Plan", buttonLink: "/payment/pro-plus", requiresBillingSelection: true, helperText: "" },
        yearly:  { usdAmount: 48, annualUsdAmount: 576, priceDescription: "per month",    buttonText: "Select Plan", buttonLink: "/payment/pro-plus", requiresBillingSelection: true, helperText: "" },
      },
      features: ["500 credits you can spend for", "Brainstorm how to grow your profile", "Analyze why contents perform well / not", "Generate content ideas", "Ask for any advice you want!"],
      planId: "pro-plus",
    },
  ];

  const plans = DEFAULT_PLANS.map((plan) => ({
    ...plan,
    title: t(`landing.pricing_plans.${plan.translationKey}.title`, plan.title),
    pricing: {
      monthly: {
        ...plan.pricing.monthly,
        price: formatPlanPrice(plan.pricing.monthly.usdAmount, i18n.language),
        priceDescription: t(
          `landing.pricing_plans.${plan.translationKey}.price_description`,
          plan.pricing.monthly.priceDescription
        ),
        buttonText: t(
          `landing.pricing_plans.${plan.translationKey}.button_text`,
          plan.pricing.monthly.buttonText
        ),
      },
      yearly: {
        ...plan.pricing.yearly,
        price: formatPlanPrice(plan.pricing.yearly.usdAmount, i18n.language),
        priceDescription: t(
          `landing.pricing_plans.${plan.translationKey}.price_description`,
          plan.pricing.yearly.priceDescription
        ),
        buttonText: t(
          `landing.pricing_plans.${plan.translationKey}.button_text`,
          plan.pricing.yearly.buttonText
        ),
        helperText: formatYearlyHelper(t, plan.translationKey, plan.pricing.yearly.annualUsdAmount),
      },
    },
    features: plan.features.map((feature, i) => {
      if (i === 0) {
        return t(`landing.pricing_features.credits_${plan.planId === "free" ? "free" : plan.planId === "pro" ? "pro" : "pro_plus"}`, feature);
      }
      const key = feature.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
      return t(`landing.pricing_features.${key}`, feature);
    }),
  }));

  return (
    <div className={`w-full ${className}`}>
      {showBillingToggle && (
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-1">
            {(["monthly", "yearly"] as BillingCycle[]).map((cycle) => (
              <button
                key={cycle}
                type="button"
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  billingCycle === cycle
                    ? "bg-white text-black shadow"
                    : "text-neutral-300 hover:text-white"
                }`}
                onClick={() => handleBillingCycleChange(cycle)}
                aria-pressed={billingCycle === cycle}
              >
                {cycle === "monthly"
                  ? t("landing.billing_monthly", "Monthly")
                  : t("landing.billing_yearly", "Yearly")}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className={`grid grid-cols-1 ${compact ? "md:grid-cols-3" : "md:grid-cols-3"} gap-6 items-stretch`}>
        {plans.map((plan, index) => (
          <motion.div
            key={plan.planId}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
            className="flex"
          >
            <div
              className={`flex flex-col rounded-xl p-6 w-full relative transition-all duration-300 ${
                compact ? "" : ""
              } ${
                plan.highlight
                  ? "bg-white/10 border-2 border-highlight"
                  : "bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20"
              }`}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {t("landing.pricing_recommended", "Recommended")}
                </div>
              )}
              <h3 className="text-3xl font-medium">{plan.title}</h3>
              <p className="mt-4">
                <span className="text-5xl font-bold">{plan.pricing[billingCycle].price}</span>
                <span className="text-neutral-400"> {plan.pricing[billingCycle].priceDescription}</span>
              </p>
              {plan.pricing[billingCycle].helperText && (
                <p className="mt-1 text-sm text-green-300">
                  {plan.pricing[billingCycle].helperText}
                </p>
              )}
              <ul className={`mt-6 space-y-3 ${compact ? "text-base" : "text-lg"} text-neutral-300 flex-grow text-left`}>
                {plan.features.map((feature, i) => {
                  const isFirstLine = i === 0;

                  return (
                    <li key={i} className="flex items-start">
                      {isFirstLine ? (
                        <span className="h-5 w-5 mr-2 flex-shrink-0 mt-1" />
                      ) : (
                        <Check className="h-5 w-5 text-green-400 mr-2 flex-shrink-0 mt-1" />
                      )}
                      <span>{feature}</span>
                    </li>
                  );
                })}
              </ul>
              <Button
                variant={plan.highlight ? "highlight" : "outline"}
                className={`w-full mt-8 ${compact ? "text-base py-2" : "text-lg py-3"}`}
                onClick={() =>
                  handlePlanSelect(
                    plan.planId,
                    plan.pricing[billingCycle].buttonLink,
                    plan.pricing[billingCycle].requiresBillingSelection
                  )
                }
                disabled={selectedPlan !== null && selectedPlan !== plan.planId}
              >
                {selectedPlan === plan.planId ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t("landing.pricing_redirecting", "Redirecting...")}
                  </>
                ) : (
                  <>
                    {plan.pricing[billingCycle].buttonText}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
